import {useCallback, useMemo, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {
  useGetEquipmentsWithSearchPaginatedQuery,
  useGetLineQuery,
  useGetLinesWithSearchQuery,
} from '@apis/base'
import {useGetProductionAreasQuery} from '@apis/settings'

interface FilterState {
  productionAreaId: string
  lineId: string
  equipmentId: string
}

interface SearchState {
  area: string
  line: string
}

const PARAM_KEYS = {
  PRODUCTION_AREA: 'productionAreaId',
  LINE: 'lineId',
  EQUIPMENT: 'equipmentId',
} as const

interface UseHierarchyFiltersReturn {
  filterState: FilterState
  updateParam: (key: string, value: string) => void
  searchHandlers: {
    handleAreaSearch: (search: string) => void
    handleLineSearch: (search: string) => void
    handleEquipmentSearch: (search: string) => void
  }
  data: {
    productionAreas: Array<{id: string; name: string}>
    lines: Array<{id: string; name: string}>
    equipments: Array<{id: string; name: string}>
  }
  loading: {
    isFetchingAreas: boolean
    isFetchingLines: boolean
    isFetchingEquipments: boolean
  }
  errors: {
    areasError: unknown
    linesError: unknown
    equipmentsError: unknown
  }
  selectedNames: {
    selectedProductionArea: string
    selectedLine: string
    selectedParentForEquipment: string
  }
  paramKeys: typeof PARAM_KEYS
}

export const useHierarchyFilters = (): UseHierarchyFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filterState: FilterState = useMemo(
    () => ({
      productionAreaId: searchParams.get(PARAM_KEYS.PRODUCTION_AREA) ?? '',
      lineId: searchParams.get(PARAM_KEYS.LINE) ?? '',
      equipmentId: searchParams.get(PARAM_KEYS.EQUIPMENT) ?? '',
    }),
    [searchParams],
  )

  const [searchState, setSearchState] = useState<SearchState>({
    area: '',
    line: '',
  })

  const {equipments, isFetchingEquipments, equipmentsError, handleEquipmentSearch} =
    useHierarchyEquipmentOptions({
      productionAreaId: filterState.productionAreaId,
      lineId: filterState.lineId,
      // Deep links may carry an equipment outside the first options page
      // (especially with no line narrowing it) — includeIds guarantees the
      // selected one is present so its label resolves on reload.
      selectedEquipmentId: filterState.equipmentId || undefined,
    })

  const updateParam = useCallback(
    (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams)

      if (value) {
        newParams.set(key, value)
      } else {
        newParams.delete(key)
      }

      if (key === PARAM_KEYS.PRODUCTION_AREA) {
        newParams.delete(PARAM_KEYS.LINE)
        newParams.delete(PARAM_KEYS.EQUIPMENT)
      } else if (key === PARAM_KEYS.LINE) {
        newParams.delete(PARAM_KEYS.EQUIPMENT)
      }

      setSearchParams(newParams, {replace: true})
    },
    [searchParams, setSearchParams],
  )

  const searchHandlers = {
    handleAreaSearch: (area: string): void =>
      setSearchState((prev) => ({
        ...prev,
        area,
      })),

    handleLineSearch: (line: string): void =>
      setSearchState((prev) => ({
        ...prev,
        line,
      })),

    handleEquipmentSearch,
  }
  const {
    data: productionAreasRaw,
    isFetching: isFetchingAreas,
    error: areasError,
  } = useGetProductionAreasQuery({search: searchState.area})

  const {
    data: linesPage,
    isFetching: isFetchingLines,
    error: linesError,
  } = useGetLinesWithSearchQuery({
    search: searchState.line,
    productionAreaId: filterState.productionAreaId,
    // Dropdown only needs id+name — skip the heavy machine serializer.
    lean: true,
  })

  // The selected line on its own, like the equipment above: the list is the
  // server's first page (25 lines), so a deep link to a line further down (the
  // equipment dashboard's "view signals" link) left the Line filter blank while
  // its id still narrowed the equipment list (AB#6985).
  const {data: chosenLine} = useGetLineQuery(
    {lineId: filterState.lineId},
    {skip: !filterState.lineId},
  )

  const lines = useMemo(() => {
    const seen = new Set<string>()
    return [...(chosenLine ? [chosenLine] : []), ...(linesPage ?? [])]
      .filter((line) => !seen.has(line.id) && seen.add(line.id))
      .map((line) => ({id: line.id, name: line.name}))
  }, [chosenLine, linesPage])

  const productionAreas = useMemo(() => {
    return Array.isArray(productionAreasRaw)
      ? productionAreasRaw
      : (productionAreasRaw?.results ?? [])
  }, [productionAreasRaw])

  const selectedNames = useMemo(() => {
    const selectedProductionArea =
      productionAreas.find((a) => a.id === filterState.productionAreaId)?.name ?? ''
    const selectedLine = lines.find((l) => l.id === filterState.lineId)?.name ?? ''
    return {
      selectedProductionArea,
      selectedLine,
      selectedParentForEquipment: selectedLine || selectedProductionArea,
    }
  }, [productionAreas, lines, filterState])

  return {
    filterState,
    updateParam,
    searchHandlers,
    data: {
      productionAreas,
      lines,
      equipments,
    },
    loading: {
      isFetchingAreas,
      isFetchingLines,
      isFetchingEquipments,
    },
    errors: {
      areasError,
      linesError,
      equipmentsError,
    },
    selectedNames,
    paramKeys: PARAM_KEYS,
  }
}

export const useHierarchyEquipmentOptions = ({
  productionAreaId,
  lineId,
  selectedEquipmentId,
}: {
  productionAreaId?: string
  lineId?: string
  selectedEquipmentId?: string
}): {
  equipments: Array<{id: string; name: string}>
  isFetchingEquipments: boolean
  equipmentsError: unknown
  handleEquipmentSearch: (search: string) => void
} => {
  const [equipmentSearch, setEquipmentSearch] = useState('')

  // The list the user browses. It is deliberately NOT narrowed to the current
  // selection: `includeIds` serialises to `id__in`, which the API treats as a
  // restriction rather than an addition, so asking for the selected id here
  // collapsed the whole dropdown to that one row. Picking an equipment then hid
  // every other equipment until you cleared it or typed a search.
  const {
    data: equipmentsPage,
    isFetching: isFetchingEquipments,
    error: equipmentsError,
  } = useGetEquipmentsWithSearchPaginatedQuery({
    search: equipmentSearch,
    productionAreaId,
    lineId,
    limit: 100,
    // Dropdown only needs id+name — skip the heavy equipment serializer.
    lean: true,
  })

  // A second, one-row fetch for the selection alone. Deep links may carry an
  // equipment outside the first options page (especially with no line narrowing
  // it), and a freshly mounted filter has no cached name for it — without this
  // the trigger renders blank on reload (AB#5520). Same two-query shape the
  // notify-action editor uses, and for the same reason.
  const {data: chosenEquipment} = useGetEquipmentsWithSearchPaginatedQuery(
    {includeIds: selectedEquipmentId ? [selectedEquipmentId] : undefined, limit: 1, lean: true},
    {skip: !selectedEquipmentId},
  )

  const equipments = useMemo(() => {
    const seen = new Set<string>()
    // Selection first, so it survives the dedupe and stays visible while a
    // search narrows the page beneath it.
    return [...(chosenEquipment?.results ?? []), ...(equipmentsPage?.results ?? [])]
      .filter((equipment) => !seen.has(equipment.id) && seen.add(equipment.id))
      .map((equipment) => ({id: equipment.id, name: equipment.name}))
  }, [chosenEquipment, equipmentsPage])

  return {
    equipments,
    isFetchingEquipments,
    equipmentsError,
    handleEquipmentSearch: setEquipmentSearch,
  }
}
